import { extend } from 'underscore';
import { merge } from '@bingads-webui/primitive-utilities';
import { convertToLocalStyle, scrollbarStyle } from '@bingads-webui-campaign-react/common-styles';

import { getGoogleImportStyle } from './google-import-page-style';

const singleListBoxWidth = 400;
const singleListBoxHeight = 300;

const globalStyleForMultiAccountGoogleImport = ({ palette = {} }) => ({
  '.BingAccountViewDetailsView, .StepSummaryDetailView, .TrackingTemplateView, .FinalUrlSuffixView': {
    border: `0 solid ${palette.neutralLight}`,
    '-webkit-box-shadow': '0 6.4px 14.4px 0 rgba(0,0,0,.132), 0 1.2px 3.6px 0 rgba(0,0,0,.108)',
    boxShadow: '0 6.4px 14.4px 0 rgba(0,0,0,.132), 0 1.2px 3.6px 0 rgba(0,0,0,.108)',
    borderRadius: 2,
    outline: 'none',
    backgroundColor: palette.white,
    padding: '10px 20px',
    width: 350,

    '& .popover-titled-content__title': {
      fontWeight: 600,
      fontSize: 16,
      marginTop: 21,
      marginBottom: 10,
    },

    '& .popover-titled-content__body': {
      '& .selected-account': {
        '& h6.truncated': {
          fontWeight: 400,
          fontSize: 12,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
  },

  '.StepSummaryDetailView, .TrackingTemplateView, .FinalUrlSuffixView': {
    width: 480,

    '& .popover-titled-content__body': {
      paddingBottom: 10,
      maxHeight: '400px',
      overflowY: 'auto',

      '& .table-container': {
        '& .table': {
          border: 0,

          '& .col-AccountName': {
            width: '50%',
          },

          '& th.column-header-leaf': {
            borderTop: '2px solid #81868B',
            borderBottom: '2px solid #81868B',
          },

          '& th[data-name="AccountName"], td.AccountName': {
            borderLeft: 0,
          },

          '& th[data-name="customizedContents"], td.customizedContents, th[data-name="urlContents"], td.urlContents': {
            borderRight: 0,
          },

          '& td.AccountName, td.customizedContents, td.urlContents': {
            verticalAlign: 'middle',
            wordBreak: 'break-word',
          },
        },
      },
    },
  },
});

const getGoogleImportStyleWithGlobal = (_, { palette = {} }) => (merge(
  getGoogleImportStyle(_, { palette }),
  {
    '@global': extend(
      {},
      globalStyleForMultiAccountGoogleImport({ palette })
    ),
  }
));

export const getMultiAccountGoogleImportStyle = (_, { palette = {} }) => ({
  ...getGoogleImportStyleWithGlobal(_, { palette }),
  multiAccountGoogleImportPage: extend(
    {},
    {
      // choose accounts
      '& .multi-account-title-grid': {
        marginLeft: 0,
        marginRight: 0,
        width: '100%',
      },

      '& .multi-account-title-grid + .row > .col-sm-9': {
        width: '100%',

        '& .multi-bing-account-grid': {
          '& .doubleListBox': {
            ...convertToLocalStyle(scrollbarStyle({ palette })),

            '& .row, .form-group': {
              margin: 0,
            },

            '& .layout-columns-container.row': {
              '& .layout-columns-element': {
                width: singleListBoxWidth,
                marginRight: 8,
              },

              '& .layout-columns-element[data-bind="visible: features.filtering || showMaximumSelectionAlert"]': {
                display: 'flex',
                flexDirection: 'column',
                height: 0,
                float: 'none',

                '& .form-group[data-bind="visible: features.filtering"]': {
                  order: 2,
                },

                '& >div[data-bind="visible: showMaximumSelectionAlert"]': {
                  order: 1,
                  marginBottom: 10.5,
                },
              },

              '& .dlb-filter-alignPlaceholder': {
                padding: 0,
                width: '100%',
                position: 'absolute',

                '& .searchbox': {
                  '& .searchbox-input': {
                    border: `1px solid ${palette.neutralSecondary}`,
                    borderBottom: 0,
                    height: 48,
                    paddingLeft: 48,
                  },

                  '& .form-control-feedback': {
                    position: 'absolute',
                    left: 0,
                    lineHeight: '46px',
                    pointerEvents: 'auto',
                  },

                  '& .filter_search_icon_small': {
                    right: 0,
                  },
                },
              },


              '& .layout-columns-element:nth-child(2)': {
                height: 'auto',
                marginTop: 45,
                padding: 0,

                '& .row.form-group': {
                  border: `1px solid ${palette.neutralSecondary}`,
                },

                '& .panel-body': {
                  height: singleListBoxHeight - 48 - 36,
                },
              },

              '& .layout-columns-element:nth-child(3)': {
                border: `1px solid ${palette.neutralSecondary}`,
                padding: 0,
                height: singleListBoxHeight,

                '& .panel-body': {
                  height: singleListBoxHeight - 36,
                },
              },

              '& .panel-form': {
                position: 'relative',

                '& .panel-heading': {
                  padding: 0,

                  '& .panel-title': {
                    fontSize: 14,
                    height: 'auto',
                    lineHeight: '36px',
                    padding: '0 8px',
                    margin: 0,
                  },
                },

                '& .panel-body': {
                  padding: 0,
                  overflowY: 'auto',

                  '& .dlb-selectAll, .dlb-deselectAll': {
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    padding: '5px 10px',

                    '& .remove.arrow-button, .enabled.arrow-button': {
                      display: 'none',
                    },

                    '& span': {
                      color: palette.themePrimary,
                    },
                  },
                },
              },

              '& .col-sm-12': {
                padding: 0,
              },
            },

            '& .dlb-source, & .dlb-selected': {
              '&:focus': {
                outline: 'none',
              },
            },

            '& .k-state-focused': {
              border: `1px solid ${palette.neutralSecondary}`,
            },

            '& .k-state-selected, & .selected': {
              backgroundColor: palette.neutralLight,
            },

            '& .dlbItem': {
              padding: '0 8px',
              position: 'relative',

              '&:hover': {
                background: palette.neutralLighter,
              },

              '& .dlbItem-status-icon': {
                display: 'inline-block',
                verticalAlign: 'middle',
                paddingRight: 5,
              },

              '& .dlbItem-text': {
                display: 'inline-block',
                verticalAlign: 'middle',
              },

              '& .dlbItem-content': {
                minHeight: 36,
                lineHeight: '36px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '90%',
              },

              '& .dlbItem-actions': {
                position: 'absolute',
                top: 8,
                right: 8,

                '& .arrow-button:focus': {
                  textDecoration: 'none',
                  border: `1px solid ${palette.neutralSecondary}`,
                },
              },

            },

            '& .pager': {
              height: 'auto',

              '& .pager-page': {
                float: 'right',

                '& span': {
                  marginLeft: 6,
                  verticalAlign: 'baseline',
                },
              },
            },
          },
        },
      },

      '& .truncated': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },

      // multi google account/campaign grid
      '& .multi-google-account-grid, & .multi-google-campaign-grid': {
        '& .table-container': {
          '& .text-center.column-header-leaf': {
            padding: '8px 10px 8px 0',
          },

          '& .column-header-leaf[data-name="AccountName"], .column-header-leaf[data-name="Name"]': {
            borderRight: `1px solid ${palette.neutralLight}`,
          },

          '& tr': {
            '& td.truncated': {
              borderRight: `1px solid ${palette.neutralLight}`,
            },
          },

          '& tr:hover, tr.row-selected': {
            '& td.truncated': {
              borderRight: `1px solid ${palette.white}`,
            },
          },
        },

        '& .bing-account-selector, & .google-account-selector': {
          width: '100%',
          maxWidth: 300,

          '& .ba-dropdown-selector': {
            position: 'relative',

            '& .dropdown-toggle': {
              width: '100%',
              border: `1px solid ${palette.neutralSecondary}`,

              '& .truncated': {
                height: 30,
                lineHeight: '30px',
                paddingRight: 25,
              },

              '& .glyphicon-triangle-bottom': {
                position: 'absolute',
                top: '50%',
                right: 20,
                transform: 'translate(0, -50%)',

                '&:before': {
                  content: '"\\E70D"',
                },
              },
            },
          },
        },

        '& .ba-dropdown-list-popup, & .ba-dropdown-selector.open': {
          position: 'relative',

          '& .dropdown-menu': {
            opacity: 1,
            visibility: 'visible',
            zIndex: 1,
            padding: '10px 15px',
            width: 300,
            top: '100%',

            '& .search-box-container': {
              '& .search-box__input-group': {
                width: '100%',

                '& .search-box__input': {
                  width: '100%',
                },
              },
            },

            '& .list-selector-container': {
              '& .list-selector-content': {
                '& >div': {
                  padding: '8px 0',
                },

                '& .list-selector-item': {
                  cursor: 'pointer',

                  '&:hover': {
                    background: palette.neutralLighter,
                  },

                  '&.selected': {
                    background: palette.neutralLight,
                  },
                },

                '& .list-selector-option': {
                  '& >.truncated': {
                    display: 'block',
                  },
                },
              },
            },
          },
        },
      },

      // multi google campaign grid
      '& .multi-google-campaign-grid': {
        '& div[role="radiogroup"]': {
          '& .radio-inline:nth-child(2)': {
            display: 'table-cell',
            marginLeft: 0,
          },
        },

        '& .table-container': {
          '& td.text-center': {
            padding: '8px 10px 8px 0',
          },
        },

        '& .show-paused-filter': {
          '& .checkbox': {
            display: 'inline-block',
          },
        },

        '& .importCampaignsGrid + .alert-info': {
          display: 'block',
        },
      },
    }
  ),
});
